<template>
  <v-flex
    v-if="isLoadingFinished"
    id="sheet-container"
    md10
    offset-md1
    sm12
    offset-sm1
    mt-3
    :mx-3="xsOnly"
  >
    <a
      class="link_iconed"
      @click.prevent="goBack"
    >
      <v-icon>keyboard_arrow_left</v-icon> {{ $t('sheet.cancel_button') }}</a>
    <v-layout column>
      <DescriptionInstruction :read-only="readOnly" />
      <v-divider class="my-4" />
      <familyIntroduction
        :family-guide="sheetOneMeeting.familyGuide"
        :age-resource="ageResource"
        :relation-resource="relationResource"
        :changed-names="changedNames.newNames"
      />
      <v-divider class="mt-5 mb-4" />
      <familyHistory
        :resource-data="sheetOneMeeting"
        :read-only="readOnly"
      />
      <v-divider class="my-4" />
      <familyEverydayLife
        v-if="isCurrentEdition"
        :resource-data="sheetOneMeeting"
        :read-only="readOnly"
      />
      <familyBeauty
        v-else
        :resource-data="sheetOneMeeting.familyResources"
        :reflection-resource-data="sheetOneReflection.resources"
        :read-only="readOnly"
      />
      <v-divider class="my-4" />
      <familyImportantNeeds
        class="mb-4"
        :important-needs="sheetTwo.importantNeeds.list"
        :read-only="readOnly"
      />
      <previewDescription
        :important-needs="sheetTwo.importantNeeds.list"
        :family-guide="sheetOneMeeting.familyGuide"
        :age-resource="ageResource"
        :relation-resource="relationResource"
        :changed-names="changedNames.newNames"
      />
      <sheetEndButtons
        class="mb-3"
        :sheet-type="'description'"
        :family-id="$route.params.familyId"
        :to="nextButtonLinkName"
        :next-section-name="nextSectionName"
        :read-only="readOnly"
        :scroll-button="true"
        :can-do-audit-in-area="canDoAuditInArea"
        :can-do-audit-on-national="canDoAuditOnNational"
        :national-correcting-permission="hasPermissionsForNationalCorrecting"
      />
    </v-layout>
    <leaveConfirmDialog ref="confirm" />
    <FamilyAudit
      v-if="showAuditCard"
      :can-add-comment="canAddComment"
      centered
      :family-id="$route.params.familyId"
      :sheet-type="'description'"
      :comment-level="commentLevel"
      :has-permissions-to-add-comment="hasPermissionsToAddComment"
    />
  </v-flex>
  <PageLoader v-else />
</template>

<script>
import DescriptionInstruction from '@/components/description/DescriptionInstruction';
import familyIntroduction from '@/components/description/familyIntroduction';
import familyHistory from '@/components/description/familyHistory';
import familyEverydayLife from '@/components/description/familyEverydayLife';
import familyBeauty from '@/components/description/familyBeauty';
import familyImportantNeeds from '@/components/description/familyImportantNeeds';
import previewDescription from '@/components/description/previewDescription';
import sheetEndButtons from '@/components/widgets/sheet/sheetEndButtons';
import leaveConfirmDialog from '@/components/widgets/LeaveConfirmDialog';
import FamilyAudit from '@/components/audit/FamilyAudit';

import sheetBeforeLeave from '@/mixins/sheetBeforeLeave';
import auditPermissionsAndStatuses from '@/mixins/auditPermissionsAndStatuses';
import isFamilyChecked from '@/mixins/sheet/isFamilyChecked';

import permissions from '@/constants/permissions';
import PageLoader from '@/components/widgets/PageLoader';

export default {
    name: 'FamilyDescription',
    $_veeValidate: {
        validator: 'new'
    },
    components: {
        PageLoader,
        sheetEndButtons,
        leaveConfirmDialog,
        previewDescription,
        familyBeauty,
        familyEverydayLife,
        DescriptionInstruction,
        familyIntroduction,
        familyHistory,
        familyImportantNeeds,
        FamilyAudit
    },
    mixins: [sheetBeforeLeave, auditPermissionsAndStatuses, isFamilyChecked],
    beforeRouteEnter (to, from, next) {
        next((vm) => {
            vm.$store
                .dispatch('family/getFamily', { id: vm.$route.params.familyId, endpointLabel: 'family' })
                .then(({ data }) => {
                    vm.$store.dispatch('audit/getAllAuditComments');
                    vm.$store.dispatch('audit/getAuditComment', {
                        params: {
                            sheetType: 'description'
                        }
                    });

                    vm.$store
                        .dispatch('sheet/loadSheetOneResource', {
                            sheetId: 1,
                            editionName: data.edition.name
                        })
                        .finally(() => {
                            vm.isLoadingResource = false;
                        });

                    const family = vm.$store.getters['family/getFamily'];
                    const meeting = family.sheets.find((el) => el.sheet_type === 'meeting');
                    const reflection = family.sheets.find((el) => el.sheet_type === 'reflection');
                    const decision = family.sheets.find((el) => el.sheet_type === 'decision');
                    const needs = family.sheets.find((el) => el.sheet_type === 'needs');
                    meeting && vm.$store.commit('sheet/setSheetMeetingQuestions', meeting.sheet_questions);
                    reflection && vm.$store.commit('sheet/setReflectionQuestions', reflection.sheet_questions);
                    needs && vm.$store.commit('sheet/setNeedsQuestions', needs.sheet_questions);
                    vm.$store.commit('sheet/setSheetOne', {
                        meeting: meeting ? meeting.sheet_data : {},
                        reflection: reflection ? reflection.sheet_data : {},
                        decision: decision ? decision.sheet_data : {}
                    });
                    needs && vm.$store.commit('sheet/setSheetTwo', needs.sheet_data);
                })
                .finally(() => {
                    vm.isLoadingSheets = false;
                });
            vm.$store.dispatch('sheet/getSheetPermissions', vm.$route.params.familyId);
            vm.$store
                .dispatch('sheet/loadSheet', {
                    familyId: vm.$route.params.familyId,
                    sheetType: 'description'
                })
                .finally(() => {
                    vm.isDescriptionLoading = false;
                });
        });
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('description/clearDescription');
        this.$store.commit('sheet/clearSheetOne');
        this.$store.commit('family/setFamily', null);
        next();
    },
    data () {
        return {
            permissions,
            isLoadingSheets: true,
            isDescriptionLoading: true,
            isLoadingResource: true
        };
    },
    computed: {
        isLoadingFinished () {
            return !this.isDescriptionLoading && !this.isLoadingSheets && !this.isLoadingResource;
        },
        familyId () {
            return this.$route.params.familyId;
        },
        xsOnly () {
            return this.$vuetify.breakpoint.xsOnly;
        },
        sheetOneMeeting () {
            return this.$store.getters['sheet/getMeetingFields'];
        },
        sheetOneResource () {
            return this.$store.getters['sheet/getSheetOneResource'];
        },
        sheetOneReflection () {
            return this.$store.getters['sheet/getReflectionFields'];
        },
        ageResource () {
            return this.sheetOneResource.sheet_resource.find((el) => {
                return el.id === 1;
            }).resources;
        },
        relationFemaleResource () {
            return this.sheetOneResource.sheet_resource.find((el) => {
                return el.id === 2;
            }).resources;
        },
        relationMaleResource () {
            return this.sheetOneResource.sheet_resource.find((el) => {
                return el.id === 3;
            }).resources;
        },
        relationResource () {
            return this.relationFemaleResource.concat(this.relationMaleResource);
        },
        sheetTwo () {
            return this.$store.getters['sheet/getSheetTwo'];
        },
        changedNames () {
            return this.sheetTwo.changedNames;
        },
        sheetPermissions () {
            return this.$store.getters['sheet/getSheetPermissions'];
        },
        readOnly () {
            if (
                !this.canEditSheets ||
        (this.isFamilyVerifiedOnNational && !this.hasPermissionsForNationalCorrecting) ||
        !this.isCurrentEdition ||
        !this.isFamilyChecked
            ) {
                return true;
            }
            return !this.sheetPermissions.description;
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        nextButtonLinkName () {
            return this.isCurrentEdition ? 'preview-for-donor' : 'family-card';
        },
        nextSectionName () {
            return this.isCurrentEdition ? 'Podglądu Opisu' : 'Karty Rodziny';
        }
    },
    methods: {
        goBack () {
            this.$router.push({ name: 'family-card', params: { id: this.familyId } });
        }
    }
};
</script>

<style scoped></style>
